import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { FeaturesProps } from '@/types';

/**
 * Features Section Component
 * Displays the key features of the NeurosEdge platform
 */
export function Features({ features, className }: FeaturesProps) {
  return (
    <section id="features" className={cn("py-20 bg-gradient-subtle", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold">
            Powerful AI Automation Features
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our LLM-powered platform bridges the gap between human language and machine operations
          </p>
        </div>
        
        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Individual Feature Card Component
 */
interface FeatureCardProps {
  feature: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  };
}

function FeatureCard({ feature }: FeatureCardProps) {
  const IconComponent = feature.icon;
  
  return (
    <Card className="gradient-card border-border/10 shadow-card hover:shadow-elegant transition-all duration-300 hover:scale-105">
      <CardContent className="p-6 space-y-4">
        {/* Icon */}
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <IconComponent className="w-6 h-6 text-primary" />
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-semibold">
          {feature.title}
        </h3>
        <p className="text-muted-foreground">
          {feature.description}
        </p>
      </CardContent>
    </Card>
  );
}
